import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '../api/authService';
import { setAuthToken, removeAuthToken } from '../api/axiosConfig';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [authState, setAuthState] = useState({
    isAuthenticated: null,
    isLoading: true,
    user: null,
  });

  // Функция выхода из системы. Очищает токен и состояние.
  const logout = useCallback(() => {
    authService.logout().catch(err => console.error("Server logout failed, clearing client state.", err));
    removeAuthToken();
    // Очищаем флаг rememberMe при выходе
    localStorage.removeItem('rememberMe');
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
    });
  }, []);

  // **ИСПРАВЛЕННАЯ ФУНКЦИЯ**
  // Эта функция теперь отвечает только за загрузку пользователя, если токен уже есть.
  // Она не пытается обновлять токен самостоятельно, доверяя это interceptor'у.
  const loadUser = useCallback(async () => {
    // Сразу устанавливаем isLoading в true в начале операции
    setAuthState(prevState => ({ ...prevState, isLoading: true }));
    try {
      // Пытаемся обновить токен. Это самый надежный способ проверить сессию.
      const refreshResponse = await authService.refreshToken();
      setAuthToken(refreshResponse.data.accessToken);

      // Если обновление токена прошло успешно, запрашиваем профиль
      const profileResponse = await authService.getProfile();
      
      // Устанавливаем, что пользователь аутентифицирован
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: profileResponse.data,
      });

    } catch (err) {
      // Проверяем, установлен ли флаг rememberMe
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      
      if (rememberMe) {
        // Если rememberMe установлен, не выходим автоматически
        // Просто устанавливаем isLoading в false и оставляем пользователя в системе
        // Токен будет обновлен при следующем запросе через interceptor
        setAuthState(prevState => ({...prevState, isLoading: false}));
      } else {
        // Если rememberMe не установлен, выходим как обычно
        logout(); // Используем централизованную функцию logout
        // Устанавливаем isLoading в false, даже если была ошибка
        setAuthState(prevState => ({...prevState, isLoading: false}));
      }
    }
  }, [logout]); // logout теперь в зависимостях

  // Этот эффект запускается один раз при монтировании компонента
  useEffect(() => {
    const handleAuthError = () => {
        // Проверяем, установлен ли флаг rememberMe перед автоматическим выходом
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        if (!rememberMe) {
            logout();
        }
        // Если rememberMe установлен, не выходим - токен будет обновлен при следующем запросе
    };
    window.addEventListener('auth-error', handleAuthError);

    // Пытаемся загрузить пользователя при первоначальной загрузке приложения
    loadUser();

    return () => {
        window.removeEventListener('auth-error', handleAuthError);
    };
  }, [loadUser, logout]); // loadUser и logout в зависимостях

  // **ИСПРАВЛЕННАЯ ФУНКЦИЯ**
  // Функция входа в систему
  const login = async (email, password, rememberMe) => {
    try {
      // 1. Отправляем запрос на логин
      const res = await authService.login({ email, password, rememberMe });
      
      // 2. Сохраняем флаг rememberMe в localStorage
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }
      
      // 3. Устанавливаем полученный токен доступа
      setAuthToken(res.data.accessToken);
      
      // 4. Устанавливаем состояние аутентификации с данными пользователя из ответа
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: res.data.user,
      });
      
      return res.data;
    } catch (err) {
      // Если логин не удался, вызываем logout, чтобы очистить все следы
      logout();
      throw err; // Пробрасываем ошибку дальше, чтобы LoginPage мог ее обработать
    }
  };

  const register = async (userData) => {
    const res = await authService.register(userData);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, register, logout, loadUser }}>
      {authState.isLoading ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-700">
            {t('auth_checking')}
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};

export default AuthContext;